import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// Legend.xyz inspired design styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#F5F5F5', // Light grey background
    padding: 24, // md spacing
    fontFamily: 'Helvetica Neue, Arial, sans-serif',
  },
  
  // Page content with proper spacing
  pageContent: {
    flex: 1,
    justifyContent: 'space-between',
  },
  
  // White rectangles with curved edges and shadow
  whiteCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12, // Curved edges
    padding: 24, // md spacing
    marginBottom: 24, // md spacing
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    flex: 1,
  },
  
  // Typography following Legend.xyz specs
  h1: {
    fontSize: 36, // h2 size
    fontWeight: 'bold',
    color: '#000000', // primary color
    marginBottom: 16, // sm spacing
  },
  
  h2: {
    fontSize: 24, // h3 size
    fontWeight: 'bold',
    color: '#000000', // primary color
    marginBottom: 16, // sm spacing
  },
  
  h3: {
    fontSize: 16, // body size
    fontWeight: 'bold',
    color: '#000000', // primary color
    marginBottom: 12,
  },
  
  bodyText: {
    fontSize: 16, // body size
    color: '#000000', // text color
    lineHeight: 1.5,
    marginBottom: 8,
  },
  
  mutedText: {
    fontSize: 14, // small size
    color: '#666666', // mutedText color
    lineHeight: 1.4,
  },
  
  // Data display
  dataGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginTop: 16, // sm spacing
  },
  
  dataItem: {
    width: '48%',
    marginBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  dataLabel: {
    fontSize: 14, // small size
    color: '#666666', // mutedText color
    fontWeight: 'normal',
  },
  
  dataValue: {
    fontSize: 16, // body size
    color: '#000000', // primary color
    fontWeight: 'bold',
  },
  
  // Chart styling
  chartContainer: {
    marginTop: 16, // sm spacing
    alignItems: 'center',
  },
  
  chartImage: {
    width: '100%',
    height: 180,
    borderRadius: 8,
  },
  
  // Footer
  footer: {
    position: 'absolute',
    bottom: 24, // md spacing
    left: 24, // md spacing
    right: 24, // md spacing
    textAlign: 'center',
    fontSize: 12,
    color: '#666666', // mutedText color
  },
});

const FancyReport = ({ loanData, chartImage, paymentChartImage }) => {
  const currentDate = new Date().toLocaleDateString('nl-NL');
  
  return (
    <Document>
      {/* Page 1: Overview & Data */}
      <Page size="A4" style={styles.page}>
        <View style={styles.pageContent}>
          {/* Section 1: Document Overview */}
          <View style={styles.whiteCard}>
            <Text style={styles.h1}>Slimmer af Financieel Rapport</Text>
            <Text style={styles.bodyText}>
              Dit rapport geeft een overzicht van uw studieschuld en de gekozen aflosstrategie. 
              Hieronder vindt u een samenvatting van uw ingevoerde gegevens en de belangrijkste uitkomsten van uw aflosplan.
            </Text>
            <Text style={styles.mutedText}>
              Gegenereerd op {currentDate} voor {loanData.userName || 'Gebruiker'}
            </Text>
          </View>

          {/* Section 2: Input Data Summary */}
          <View style={styles.whiteCard}>
            <Text style={styles.h2}>Uw Gegevens</Text>
            <View style={styles.dataGrid}>
              <View style={styles.dataItem}>
                <Text style={styles.dataLabel}>Totale schuld</Text>
                <Text style={styles.dataValue}>€{loanData.totalDebt.toLocaleString('nl-NL')}</Text>
              </View>
              <View style={styles.dataItem}>
                <Text style={styles.dataLabel}>Aflosstelsel</Text>
                <Text style={styles.dataValue}>{loanData.repaymentPlan} jaar</Text>
              </View>
              <View style={styles.dataItem}>
                <Text style={styles.dataLabel}>Minimale aflossing</Text>
                <Text style={styles.dataValue}>€{loanData.minPayment.toLocaleString('nl-NL')}</Text>
              </View>
              <View style={styles.dataItem}>
                <Text style={styles.dataLabel}>Maandelijkse aflossing</Text>
                <Text style={styles.dataValue}>€{loanData.monthlyPayment.toLocaleString('nl-NL')}</Text>
              </View>
              <View style={styles.dataItem}>
                <Text style={styles.dataLabel}>Rentepercentage</Text>
                <Text style={styles.dataValue}>{(loanData.annualRate * 100).toFixed(2)}%</Text>
              </View>
              <View style={styles.dataItem}>
                <Text style={styles.dataLabel}>Looptijd</Text>
                <Text style={styles.dataValue}>{loanData.payoffYears} jaar</Text>
              </View>
              <View style={styles.dataItem}>
                <Text style={styles.dataLabel}>Totale kosten</Text>
                <Text style={styles.dataValue}>€{loanData.totalCost.toLocaleString('nl-NL')}</Text>
              </View>
              <View style={styles.dataItem}>
                <Text style={styles.dataLabel}>Betaald aan rente</Text>
                <Text style={styles.dataValue}>€{loanData.interestAtEnd.toLocaleString('nl-NL')}</Text>
              </View>
              {loanData.enableInvestment && (
                <>
                  <View style={styles.dataItem}>
                    <Text style={styles.dataLabel}>Investeringspercentage</Text>
                    <Text style={styles.dataValue}>{(loanData.investmentPercentage * 100).toFixed(1)}%</Text>
                  </View>
                  <View style={styles.dataItem}>
                    <Text style={styles.dataLabel}>Toegevoegde waarde</Text>
                    <Text style={styles.dataValue}>
                      {loanData.addedInvestmentValue > 0 ? '+' : ''}€{Math.round(loanData.addedInvestmentValue).toLocaleString('nl-NL')}
                    </Text>
                  </View>
                </>
              )}
            </View>
          </View>
        </View>
        <Text style={styles.footer}>
          Pagina 1 van 2 - Slimmer af Financieel Rapport
        </Text>
      </Page>

      {/* Page 2: Charts & Analysis */}
      <Page size="A4" style={styles.page}>
        <View style={styles.pageContent}>
          {/* Section 1: Main Chart */}
          <View style={styles.whiteCard}>
            <Text style={styles.h2}>Aflossing Over Tijd</Text>
            <View style={styles.chartContainer}>
              {chartImage ? (
                <Image style={styles.chartImage} src={chartImage} />
              ) : (
                <View style={{ height: 180, justifyContent: 'center', alignItems: 'center' }}>
                  <Text style={styles.mutedText}>Grafiek kon niet worden weergegeven</Text>
                </View>
              )}
            </View>
          </View>

          {/* Section 2: Analysis & Recommendations */}
          <View style={styles.whiteCard}>
            <Text style={styles.h2}>Analyse & Aanbevelingen</Text>
            
            <Text style={styles.h3}>Uw Aflosstrategie</Text>
            <Text style={styles.bodyText}>
              {loanData.enableInvestment 
                ? `U heeft gekozen voor een gecombineerde strategie van aflossen en investeren. Maandelijks betaalt u €${loanData.monthlyPayment.toLocaleString('nl-NL')}, waarvan ${(loanData.investmentPercentage * 100).toFixed(1)}% wordt geïnvesteerd in de aandelenmarkt.`
                : `U heeft gekozen voor een conservatieve strategie van alleen aflossen. Maandelijks betaalt u €${loanData.monthlyPayment.toLocaleString('nl-NL')} volledig voor aflossing van uw studieschuld.`
              }
            </Text>
            
            <Text style={styles.h3}>Investeringsanalyse</Text>
            {loanData.enableInvestment ? (
              <Text style={styles.bodyText}>
                {loanData.addedInvestmentValue > 0 
                  ? `Uw investeringsstrategie levert een positief rendement op van €${Math.round(loanData.addedInvestmentValue).toLocaleString('nl-NL')}. Dit betekent dat u deze hoeveelheid bespaart ten opzichte van alleen aflossen.`
                  : `Uw investeringsstrategie levert een negatief rendement op van €${Math.round(Math.abs(loanData.addedInvestmentValue)).toLocaleString('nl-NL')}. Dit betekent dat u deze hoeveelheid meer betaalt dan bij alleen aflossen.`
                }
              </Text>
            ) : (
              <Text style={styles.bodyText}>
                U heeft gekozen voor een conservatieve aanpak zonder investeringen. Dit biedt zekerheid maar mogelijk minder rendement dan een gecombineerde strategie.
              </Text>
            )}
            
            <Text style={styles.h3}>Aanbevelingen</Text>
            {loanData.payoffYears > 15 && (
              <Text style={styles.bodyText}>
                • Overweeg een hoger maandbedrag om de looptijd te verkorten en rentekosten te besparen
              </Text>
            )}
            {loanData.interestAtEnd > loanData.totalDebt * 0.3 && (
              <Text style={styles.bodyText}>
                • De rentekosten zijn aanzienlijk. Een agressievere aflosstrategie kan duizenden euro's besparen
              </Text>
            )}
            {loanData.monthlyPayment < 300 && (
              <Text style={styles.bodyText}>
                • Met een hogere maandelijkse betaling kunt u aanzienlijk besparen op rentekosten
              </Text>
            )}
            {loanData.enableInvestment && loanData.addedInvestmentValue < 0 && (
              <Text style={styles.bodyText}>
                • Uw huidige investeringsstrategie levert verlies op. Overweeg een lagere investeringsratio
              </Text>
            )}
            {!loanData.enableInvestment && loanData.payoffYears > 10 && (
              <Text style={styles.bodyText}>
                • Overweeg een gedeeltelijke investeringsstrategie voor mogelijk hoger rendement
              </Text>
            )}
            
            <Text style={styles.h3}>Conclusie</Text>
            <Text style={styles.bodyText}>
              {loanData.payoffYears <= 15 
                ? 'U bent goed op weg om uw lening snel af te betalen. Uw strategie is efficiënt en kosteneffectief.'
                : 'Er zijn mogelijkheden om uw aflosstrategie te optimaliseren. Overweeg de bovenstaande aanbevelingen om kosten te besparen.'
              }
            </Text>
          </View>
        </View>
        <Text style={styles.footer}>
          Pagina 2 van 2 - Slimmer af Financieel Rapport
        </Text>
      </Page>
    </Document>
  );
};

export default FancyReport;
